<template>
  <v-layout column>
    <kernel-order-info-view
      :loading-error="loadingError"
      :loading="isLoading"
      :order="order"
      class="mt-2"
    />

    <vbt-tabs
      v-model="tab"
      :loading="tabsLoading"
      :tabs="tabs"
      @click:tab="onTabClick"
    >
      <template #items>
        <kernel-order-items-list
          :items="orderItems"
          :error="loadingError"
        />
      </template>
      <template #history>
        <kernel-order-history-list
          :items="orderHistory"
          :error="historyLoadingError"
        />
      </template>
      <template #files>
        <kernel-order-files-list
          :items="files"
          :error="loadingError"
        />
      </template>
      <template #attrs>
        <kernel-order-attributes
          :items="orderAttributes"
          :error="attributesLoadingError"
        />
      </template>
    </vbt-tabs>
  </v-layout>
</template>

<script>
import { mapState, mapActions } from 'vuex';

import { useDataLoading, useStateFlag, useErrorBoundary } from '@mixins/factories';

import { wrapToLoadingFn } from '@helpers';

import { namespace as OrdersNamespace, ActionTypes as OrdersActionTypes } from '../store.types';

import KernelOrderInfoView from '../_components/KernelOrderInfoView.vue';
import KernelOrderItemsList from '../_components/KernelOrderItemsList.vue';
import KernelOrderHistoryList from '../_components/KernelOrderHistoryList.vue';
import KernelOrderFilesList from '../_components/KernelOrderFilesList.vue';
import KernelOrderAttributes from '../_components/KernelOrderAttributes.vue';

const dataLoadingOptions = {
  getterName: 'getOrder', searchable: false, paged: false, parameterize: true,
};

const tabs = Object.freeze([
  { text: 'Order items', key: 'items' },
  { text: 'History', key: 'history' },
  { text: 'Files', key: 'files' },
  { text: 'Attributes', key: 'attrs' },
]);

export default {
  name: 'KernelOrder',
  components: {
    KernelOrderInfoView,
    KernelOrderItemsList,
    KernelOrderHistoryList,
    KernelOrderFilesList,
    KernelOrderAttributes,
  },
  mixins: [
    useDataLoading(dataLoadingOptions),
    useStateFlag('isHistoryLoading', 'setHistoryLoading'),
    useErrorBoundary({
      errorStateName: 'historyLoadingError',
      setterName: 'setHistoryLoadingError',
    }),
    useStateFlag('isAttributesLoading', 'setAttributesLoading'),
    useErrorBoundary({
      errorStateName: 'attributesLoadingError',
      setterName: 'setAttributesLoadingError',
    }),
  ],
  data: () => ({ tab: 'items', tabs }),
  computed: {
    ...mapState(OrdersNamespace, {
      order: ({ order }) => order,
      orderHistory: ({ orderHistory }) => orderHistory,
      orderAttributes: ({ orderAttributes }) => orderAttributes,
      orderItems: ({ order: { items } }) => items || [],
      files: ({ order: { items } }) => (items || [])
        .reduce((acc, { outerId, sides }) => {
          acc.push(...sides.map((side) => ({ ...side, outerId })));
          return acc;
        }, []),
    }),
    tabsLoading() {
      return this.isLoading || this.isHistoryLoading || this.isAttributesLoading;
    },
  },
  methods: {
    ...mapActions(OrdersNamespace, {
      getOrder(dispatch, response) {
        return dispatch(OrdersActionTypes.GET_ORDER, response);
      },
      getAttributes(dispatch) {
        this.wrapToLoadingFn({
          req: dispatch.bind({}, OrdersActionTypes.GET_ORDER_ATTRIBUTES, {
            id: this.$route.params.id,
          }),
          loadingFlagName: 'isAttributesLoading',
          onReject: this.setAttributesLoadingError,
        });
      },
      getHistory(dispatch) {
        this.wrapToLoadingFn({
          req: dispatch.bind({}, OrdersActionTypes.GET_ORDER_HISTORY, {
            id: this.$route.params.id,
          }),
          loadingFlagName: 'isHistoryLoading',
          onReject: this.setHistoryLoadingError,
        });
      },
    }),
    wrapToLoadingFn,
    onTabClick(key) {
      if (key === 'history') {
        this.getHistory();
      } else if (key === 'attrs') {
        this.getAttributes();
      }
    },
  },
};
</script>
